import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import {
  Check, X, Clock, ChevronRight, ChevronDown, ArrowUpDown,
  Loader2, Image, ClipboardCheck
} from 'lucide-react';
import { toast } from 'sonner';

const typeColors = {
  post: { bg: '#DBEAFE', text: '#1D4ED8' },
  newsletter: { bg: '#F3E8FF', text: '#7C3AED' },
  email: { bg: '#FEF3C7', text: '#D97706' },
  process: { bg: '#D1FAE5', text: '#059669' },
};

const typeLabels = {
  post: 'Post', newsletter: 'Newsletter', email: 'Email', process: 'Processo',
};

const sortLabels = {
  urgency: 'Urgenti prima',
  date: 'Per data',
  type: 'Per tipo',
};

export default function ApprovalQueuePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [contextExpanded, setContextExpanded] = useState(true);
  const [approvedCount, setApprovedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [animatingOut, setAnimatingOut] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [sortBy, setSortBy] = useState('urgency');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/approval-queue', { params: { status: 'pending' } });
      const data = (res.data || []).map(item => ({
        ...item,
        type: item.content_type || item.format || 'post',
        channel: item.channel || item.content_type || '-',
        agent: (item.agent_source || 'copywriter_agent').replace('_agent', ''),
        agentInitials: (item.agent_source || 'CW').replace('_agent', '').slice(0, 2).toUpperCase(),
        scheduledDate: item.scheduled_at
          ? new Date(item.scheduled_at).toLocaleDateString('it-CH', { day: 'numeric', month: 'short' })
          : item.created_at ? daysSince(item.created_at) : 'oggi',
        daysWaiting: item.created_at ? Math.floor((Date.now() - new Date(item.created_at)) / 86400000) : 0,
        contentText: item.content || item.preview || item.text || '',
      }));
      setItems(data);
      setTotalCount(data.length);
      if (data.length > 0 && !selectedId) setSelectedId(data[0].id);
    } catch (e) { console.error('Approval load error:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  function daysSince(dateStr) {
    const d = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
    if (d === 0) return 'oggi';
    if (d === 1) return 'ieri';
    return `${d}gg fa`;
  }

  const sortItems = (arr) => {
    switch (sortBy) {
      case 'urgency':
        return [...arr].sort((a, b) => (b.daysWaiting || 0) - (a.daysWaiting || 0));
      case 'date':
        return [...arr].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
      case 'type':
        return [...arr].sort((a, b) => (a.type || '').localeCompare(b.type || ''));
      default:
        return arr;
    }
  };

  const sortedItems = sortItems(items);

  const filters = [
    { id: 'all', label: 'Tutti', count: sortedItems.length },
    { id: 'post', label: 'Post', count: sortedItems.filter(i => i.type === 'post').length },
    { id: 'newsletter', label: 'Newsletter', count: sortedItems.filter(i => i.type === 'newsletter').length },
    { id: 'email', label: 'Email', count: sortedItems.filter(i => i.type === 'email').length },
    { id: 'process', label: 'Processi', count: sortedItems.filter(i => i.type === 'process').length },
  ];

  const filteredItems = activeFilter === 'all'
    ? sortedItems
    : sortedItems.filter(i => i.type === activeFilter);

  const selectedItem = items.find(i => i.id === selectedId);

  const goToNext = useCallback(() => {
    const idx = filteredItems.findIndex(i => i.id === selectedId);
    if (idx < filteredItems.length - 1) setSelectedId(filteredItems[idx + 1].id);
  }, [filteredItems, selectedId]);

  const handleApprove = useCallback(async () => {
    if (!selectedItem || actionLoading) return;
    setActionLoading(true);
    try {
      await api.patch(`/approval-queue/${selectedItem.id}`, { action: 'approved' });
      setAnimatingOut(selectedItem.id);
      setApprovedCount(prev => prev + 1);
      toast.success('Contenuto approvato');
      setTimeout(() => {
        setAnimatingOut(null);
        setItems(prev => prev.filter(i => i.id !== selectedItem.id));
        goToNext();
      }, 300);
    } catch { toast.error('Errore approvazione'); }
    finally { setActionLoading(false); }
  }, [selectedItem, actionLoading, goToNext]);

  const handleReject = useCallback(async () => {
    if (!selectedItem || actionLoading) return;
    setActionLoading(true);
    try {
      await api.patch(`/approval-queue/${selectedItem.id}`, { action: 'rejected' });
      toast.success('Contenuto rifiutato');
      setItems(prev => prev.filter(i => i.id !== selectedItem.id));
      goToNext();
    } catch { toast.error('Errore rifiuto'); }
    finally { setActionLoading(false); }
  }, [selectedItem, actionLoading, goToNext]);

  const handleSnooze = useCallback(async () => {
    if (!selectedItem || actionLoading) return;
    setActionLoading(true);
    try {
      const snoozeUntil = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      await api.patch(`/approval-queue/${selectedItem.id}/snooze`, { snooze_until: snoozeUntil });
      toast.success('Rimandato a domani');
      setItems(prev => prev.filter(i => i.id !== selectedItem.id));
      goToNext();
    } catch { toast.error('Errore snooze'); }
    finally { setActionLoading(false); }
  }, [selectedItem, actionLoading, goToNext]);

  const handleSelectItem = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleSelectAll = () => {
    setSelectedItems(prev => prev.length === filteredItems.length ? [] : filteredItems.map(i => i.id));
  };

  const handleBatchApprove = async () => {
    if (selectedItems.length === 0 || actionLoading) return;
    setActionLoading(true);
    try {
      await api.post('/approval-queue/batch-approve', { item_ids: selectedItems });
      toast.success(`${selectedItems.length} contenuti approvati`);
      setApprovedCount(prev => prev + selectedItems.length);
      setItems(prev => prev.filter(i => !selectedItems.includes(i.id)));
      setSelectedItems([]);
    } catch { toast.error('Errore batch approve'); }
    finally { setActionLoading(false); }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      switch (e.key.toLowerCase()) {
        case 'e': handleApprove(); break;
        case 's': handleSnooze(); break;
        case 'r': handleReject(); break;
        case 'arrowright': goToNext(); break;
        default: break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goToNext, handleApprove, handleSnooze, handleReject]);

  const progressPct = totalCount > 0 ? Math.round((approvedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" style={{ backgroundColor: '#FAFBFC' }}
        data-testid="approval-loading">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2563EB' }} />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-64px)]" data-testid="approval-page" style={{ backgroundColor: '#FAFBFC' }}>
      {/* ═══ Left Panel - List (30%) ═══ */}
      <div className="w-[30%] flex flex-col flex-shrink-0" style={{ borderRight: '1px solid #E5E7EB', backgroundColor: '#FFFFFF' }}>
        {/* Header */}
        <div className="p-4 flex-shrink-0" style={{ borderBottom: '1px solid #E5E7EB' }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-[16px] font-bold" style={{ color: '#1B2B4B' }}
                data-testid="approval-title">Approvazione</h1>
              <p className="text-[12px] mt-1" style={{ color: '#7C8CA2' }}>
                {items.length} in attesa
              </p>
            </div>
            {/* Sort */}
            <div className="relative">
              <button onClick={() => setShowSortMenu(!showSortMenu)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors hover:bg-gray-50"
                style={{ border: '1px solid #E5E7EB', color: '#6B7280' }}
                data-testid="sort-btn">
                <ArrowUpDown size={14} />{sortLabels[sortBy]}
              </button>
              {showSortMenu && (
                <div className="absolute right-0 top-full mt-1 w-40 rounded-lg bg-white shadow-lg z-10"
                  style={{ border: '1px solid #E5E7EB' }}>
                  {Object.entries(sortLabels).map(([key, label]) => (
                    <button key={key}
                      onClick={() => { setSortBy(key); setShowSortMenu(false); }}
                      className="w-full px-4 py-2 text-[13px] text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
                      style={{ color: sortBy === key ? '#2563EB' : '#1B2B4B' }}>
                      {label}
                      {sortBy === key && <Check size={14} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium" style={{ color: '#1B2B4B' }}>
                {approvedCount}/{totalCount} approvati
              </span>
              <span className="text-[11px]" style={{ color: '#7C8CA2' }}>{progressPct}%</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
              <div className="h-full rounded-full transition-all duration-300"
                style={{ backgroundColor: '#059669', width: `${progressPct}%` }} />
            </div>
          </div>
        </div>

        {/* Batch Actions */}
        {selectedItems.length > 0 && (
          <div className="p-3 flex items-center justify-between flex-shrink-0"
            style={{ backgroundColor: '#EFF6FF', borderBottom: '1px solid #E5E7EB' }}
            data-testid="approval-batch-bar">
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={selectedItems.length === filteredItems.length}
                onChange={handleSelectAll} className="w-4 h-4 rounded border-gray-300 text-blue-600" />
              <span className="text-[12px] font-medium" style={{ color: '#1B2B4B' }}>
                {selectedItems.length} selezionati
              </span>
            </div>
            <div className="flex gap-2">
              <button onClick={handleBatchApprove} disabled={actionLoading}
                className="px-3 py-1.5 rounded-md text-[11px] font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: '#059669' }}
                data-testid="approval-batch-approve">
                Approva selezionati
              </button>
              <button onClick={() => setSelectedItems([])}
                className="px-3 py-1.5 rounded-md text-[11px] font-medium transition-colors hover:bg-gray-50"
                style={{ border: '1px solid #E5E7EB', color: '#6B7280' }}>
                Annulla
              </button>
            </div>
          </div>
        )}

        {/* Select All Row */}
        {selectedItems.length === 0 && filteredItems.length > 0 && (
          <div className="px-4 py-2 flex items-center gap-2 flex-shrink-0"
            style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
            <input type="checkbox" checked={false} onChange={handleSelectAll}
              className="w-4 h-4 rounded border-gray-300 text-blue-600"
              data-testid="select-all-checkbox" />
            <span className="text-[11px]" style={{ color: '#7C8CA2' }}>Seleziona tutti</span>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex overflow-x-auto flex-shrink-0" style={{ borderBottom: '1px solid #E5E7EB' }}>
          {filters.map(f => (
            <button key={f.id} onClick={() => setActiveFilter(f.id)}
              className="px-4 py-3 text-[13px] whitespace-nowrap transition-colors"
              style={{
                color: activeFilter === f.id ? '#2563EB' : '#7C8CA2',
                borderBottom: activeFilter === f.id ? '2px solid #2563EB' : '2px solid transparent',
                marginBottom: '-1px',
              }}
              data-testid={`approval-filter-${f.id}`}>
              {f.label} ({f.count})
            </button>
          ))}
        </div>

        {/* Item List */}
        <div className="flex-1 overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
              <ClipboardCheck size={32} style={{ color: '#D1D5DB' }} className="mb-3" />
              <p className="text-[14px] font-medium" style={{ color: '#4B5563' }}>Nessun elemento</p>
              <p className="text-[12px] mt-1" style={{ color: '#7C8CA2' }}>La coda approvazione e vuota</p>
            </div>
          ) : filteredItems.map(item => {
            const tc = typeColors[item.type] || typeColors.post;
            return (
              <div key={item.id}
                className={`w-full text-left p-4 transition-all duration-300 flex items-start gap-3 ${
                  animatingOut === item.id ? 'opacity-0 -translate-x-full' : ''
                }`}
                style={{
                  borderBottom: '1px solid #E5E7EB',
                  backgroundColor: selectedId === item.id ? '#EFF6FF' : 'transparent',
                  borderLeft: selectedId === item.id ? '3px solid #2563EB' : '3px solid transparent',
                }}
                data-testid={`approval-item-${item.id}`}>
                <input type="checkbox" checked={selectedItems.includes(item.id)}
                  onChange={() => handleSelectItem(item.id)}
                  className="w-4 h-4 mt-0.5 rounded border-gray-300 text-blue-600"
                  data-testid={`select-${item.id}`} />
                <button onClick={() => setSelectedId(item.id)} className="flex-1 text-left">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-[13px] font-semibold line-clamp-2" style={{ color: '#1B2B4B' }}>
                      {item.title || item.subject || (item.contentText ? item.contentText.substring(0, 50) + '...' : 'Senza titolo')}
                    </h3>
                    {item.daysWaiting >= 3 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0"
                        style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>
                        {item.daysWaiting}gg
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="text-[11px] px-2 py-0.5 rounded" style={{ backgroundColor: '#F3F4F6', color: '#7C8CA2' }}>
                      {item.channel}
                    </span>
                    <span className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded"
                      style={{ backgroundColor: tc.bg, color: tc.text }}>
                      <span className="flex h-[14px] w-[14px] items-center justify-center rounded-full text-[7px] font-bold text-white"
                        style={{ backgroundColor: tc.text }}>
                        {item.agentInitials}
                      </span>
                      {item.agent}
                    </span>
                    <span className="text-[11px]" style={{ color: '#7C8CA2' }}>{item.scheduledDate}</span>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══ Right Panel - Preview (70%) ═══ */}
      <div className="flex-1 flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
        {selectedItem ? (
          <>
            {/* Header */}
            <div className="p-5 flex items-start justify-between flex-shrink-0" style={{ borderBottom: '1px solid #E5E7EB' }}>
              <div>
                <h2 className="text-[16px] font-bold" style={{ color: '#1B2B4B' }}
                  data-testid="approval-detail-title">
                  {selectedItem.title || selectedItem.subject || (selectedItem.contentText ? selectedItem.contentText.substring(0, 50) + '...' : 'Senza titolo')}
                </h2>
                <p className="text-[13px] mt-1" style={{ color: '#7C8CA2' }}>
                  {selectedItem.agent} - {selectedItem.channel} - {selectedItem.scheduledDate}
                </p>
              </div>
              {(() => { const tc = typeColors[selectedItem.type] || typeColors.post; return (
                <span className="text-[12px] px-3 py-1 rounded font-medium"
                  style={{ backgroundColor: tc.bg, color: tc.text }}>
                  {typeLabels[selectedItem.type] || selectedItem.type}
                </span>
              ); })()}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Context */}
              {(selectedItem.contact_name || selectedItem.linked_event_name || selectedItem.format) && (
                <div className="mb-6 rounded-lg" style={{ border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                  <button onClick={() => setContextExpanded(!contextExpanded)}
                    className="w-full flex items-center justify-between p-3 text-left">
                    <span className="text-[12px] font-medium" style={{ color: '#7C8CA2' }}>Contesto</span>
                    {contextExpanded
                      ? <ChevronDown className="w-4 h-4" style={{ color: '#7C8CA2' }} />
                      : <ChevronRight className="w-4 h-4" style={{ color: '#7C8CA2' }} />}
                  </button>
                  {contextExpanded && (
                    <div className="px-3 pb-3 space-y-1">
                      {selectedItem.contact_name && (
                        <p className="text-[12px]" style={{ color: '#6B7280' }}>
                          Contatto: <span className="font-medium">{selectedItem.contact_name}</span>
                        </p>
                      )}
                      {selectedItem.linked_event_name && (
                        <p className="text-[12px]" style={{ color: '#6B7280' }}>
                          Evento: <span className="font-medium">{selectedItem.linked_event_name}</span>
                        </p>
                      )}
                      {selectedItem.format && (
                        <p className="text-[12px]" style={{ color: '#6B7280' }}>
                          Formato: <span className="font-medium">{selectedItem.format}</span>
                        </p>
                      )}
                      <p className="text-[12px]" style={{ color: '#6B7280' }}>
                        Agente: <span className="font-medium">{selectedItem.agent}</span>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Main Content */}
              <div className="rounded-lg p-6 max-w-[640px]" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                <div className="text-[15px] leading-[1.8] whitespace-pre-wrap" style={{ color: '#1F2937' }}
                  data-testid="approval-content-preview">
                  {selectedItem.contentText || 'Anteprima non disponibile'}
                </div>
              </div>

              {/* Image preview placeholder */}
              {selectedItem.has_image && (
                <div className="mt-6 max-w-[640px] rounded-lg overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
                  <div className="w-full h-[200px] flex items-center justify-center" style={{ backgroundColor: '#F3F4F6' }}>
                    <div className="text-center">
                      <Image size={48} style={{ color: '#9CA3AF' }} className="mx-auto mb-3" />
                      <p className="text-[13px] font-medium" style={{ color: '#6B7280' }}>Immagine allegata</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Bar */}
            <div className="p-4 flex items-center justify-between flex-shrink-0"
              style={{ borderTop: '1px solid #E5E7EB', backgroundColor: '#FFFFFF' }}>
              <div className="flex items-center gap-3">
                <button onClick={handleApprove} disabled={actionLoading}
                  className="flex items-center gap-2 px-7 py-3 rounded-lg text-[15px] font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: '#059669' }}
                  data-testid="approve-btn">
                  <Check size={18} />Approva e pubblica
                </button>
                <button
                  className="px-5 py-3 rounded-lg text-[14px] font-medium transition-colors hover:bg-gray-50"
                  style={{ border: '1px solid #D1D5DB', color: '#374151' }}
                  data-testid="edit-btn">
                  Modifica
                </button>
                <button onClick={handleSnooze} disabled={actionLoading}
                  className="flex items-center gap-1.5 px-5 py-3 rounded-lg text-[14px] font-medium transition-colors hover:bg-amber-50 disabled:opacity-50"
                  style={{ border: '1px solid #FCD34D', color: '#D97706' }}
                  data-testid="snooze-btn">
                  <Clock size={16} />Rimanda
                </button>
                <button onClick={handleReject} disabled={actionLoading}
                  className="px-5 py-3 rounded-lg text-[14px] font-medium transition-colors hover:bg-red-50 disabled:opacity-50"
                  style={{ border: '1px solid #FCA5A5', color: '#DC2626' }}
                  data-testid="reject-btn">
                  Rifiuta
                </button>
              </div>
              <button onClick={goToNext}
                className="px-5 py-3 rounded-lg text-[14px] font-medium transition-colors hover:bg-blue-50 flex items-center gap-2"
                style={{ border: '1px solid #2563EB', color: '#2563EB' }}
                data-testid="next-btn">
                Prossimo <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Keyboard Shortcuts */}
            <div className="px-4 pb-3 text-center flex-shrink-0" style={{ backgroundColor: '#FFFFFF' }}>
              <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
                E = Approva - M = Modifica - S = Rimanda - R = Rifiuta - &rarr; = Prossimo
              </p>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center">
            <ClipboardCheck size={48} style={{ color: '#D1D5DB' }} className="mb-4" />
            <p className="text-[15px] font-medium" style={{ color: '#4B5563' }}>
              {items.length === 0 ? 'Nessun contenuto in attesa' : 'Seleziona un elemento'}
            </p>
            <p className="text-[13px] mt-1" style={{ color: '#7C8CA2' }}>
              {items.length === 0
                ? 'Quando gli agenti produrranno nuovi contenuti, li vedrai qui'
                : 'Scegli un contenuto dalla lista per la revisione'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
